/**
 * DeclinedCard404DE - Creative 404 Page with Credit Card Declined Theme (German)
 * 
 * A fun, on-brand 404 page for German credit card comparison site that plays on
 * the credit card declined concept. Features an animated red credit card with an
 * X mark and maintains the fintech aesthetic.
 * 
 * Features:
 * - Animated red "declined" credit card with rotating entrance
 * - X mark overlay with spring animation
 * - Fun fact about credit card numbers
 * - Smooth CSS-only animations (no animation library in the shared bundle)
 * - Fully responsive design
 * - German copy throughout
 * 
 * Usage:
 * ```tsx
 * <DeclinedCard404DE onNavigateHome={() => navigate('/')} />
 * ```
 */

import { CreditCard } from 'lucide-react';
import { Button } from './ui/button';

interface DeclinedCard404DEProps {
  onNavigateHome: () => void;
}

export function DeclinedCard404DE({ onNavigateHome }: DeclinedCard404DEProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center px-4 py-16">
      <div className="max-w-2xl w-full text-center">
        
        {/* Animated Declined Card */}
        <div className="mb-8 animate-in fade-in zoom-in-95 duration-500">
          <div className="relative inline-block">
            {/* Credit Card Background */}
            <div className="w-80 h-48 bg-gradient-to-br from-red-500 to-red-700 rounded-2xl shadow-2xl transform rotate-3 relative overflow-hidden">
              {/* Card Overlay */}
              <div className="absolute inset-0 bg-black/10" />
              
              {/* Chip */}
              <div className="absolute top-6 left-6">
                <div className="w-12 h-10 bg-gradient-to-br from-yellow-200 to-yellow-400 rounded-sm opacity-80" />
              </div>
              
              {/* Card Details */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-white/90 text-2xl mb-2">
                  •••• •••• •••• 404
                </div>
                <div className="text-white/70 text-sm">
                  SEITE ABGELEHNT
                </div>
              </div>
            </div>
            
            {/* X Mark Overlay with Spring Animation */}
            <div
              className="absolute -top-4 -right-4 w-24 h-24 bg-red-600 rounded-full flex items-center justify-center shadow-xl animate-in zoom-in duration-300 fill-mode-backwards"
              style={{ animationDelay: "300ms" }}
            >
              <svg 
                className="w-16 h-16 text-white" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor" 
                strokeWidth="3"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  d="M6 18L18 6M6 6l12 12" 
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Error Message */}
        <div
          className="space-y-4 mb-8 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-backwards"
          style={{ animationDelay: "400ms" }}
        >
          <h1 className="text-gray-900">
            Hoppla! Seite wurde abgelehnt
          </h1>
          <div className="max-w-lg mx-auto space-y-2">
            <p className="text-xl text-gray-600">
              <strong>Fehlercode: 404</strong>
            </p>
            <p className="text-gray-600">
              Die Seite, die Sie suchen, existiert nicht. Möglicherweise ist sie abgelaufen oder die URL ist falsch?
            </p>
          </div>
        </div>

        {/* Fun Fact Card */}
        <div
          className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 mb-8 max-w-lg mx-auto animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-backwards"
          style={{ animationDelay: "600ms" }}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">💡</span>
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600">
                <strong className="text-gray-900">Wussten Sie schon:</strong>
                <br />
                Die meisten Kreditkarten in Deutschland haben 16 Ziffern, aber diese Seite hat nur 3: 404.
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action Button */}
        <div
          className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-backwards"
          style={{ animationDelay: "700ms" }}
        >
          <Button
            onClick={onNavigateHome}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 gap-2"
          >
            <CreditCard className="w-5 h-5" />
            Alle deutschen Kreditkarten ansehen
          </Button>
        </div>
      </div>
    </div>
  );
}
